/**
 * BookkeeperHistoryPanel
 * Reusable panel showing prior-month T1/T2/T3 completion history.
 * Used by: BookkeepingManagerDashboard (Jany) and BookkeeperDashboard (Irit).
 *
 * Props:
 *   filterEmail  — if provided, only show rows for this bookkeeper email (Irit's self-view)
 *   title        — panel heading (default "Prior Month History")
 *   id           — optional HTML id for anchor linking (default "history-panel")
 */
import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { ChevronDown, ChevronRight, Clock, CheckCircle2, Users } from "lucide-react";

const MONTH_NAMES = [
  "", "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const TIER_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  T1: { bg: "oklch(0.55 0.16 85 / 12%)",  text: "oklch(0.40 0.14 85)",  border: "oklch(0.55 0.16 85 / 30%)"  },
  T2: { bg: "oklch(0.45 0.18 240 / 12%)", text: "oklch(0.35 0.16 240)", border: "oklch(0.45 0.18 240 / 30%)" },
  T3: { bg: "oklch(0.45 0.20 300 / 12%)", text: "oklch(0.35 0.18 300)", border: "oklch(0.45 0.20 300 / 30%)" },
};

const TIER_LABELS: Record<string, string> = {
  T1: "Bank Feed",
  T2: "Coding",
  T3: "ATB / C-P&L",
};

interface Props {
  filterEmail?: string;
  title?: string;
  id?: string;
}

export default function BookkeeperHistoryPanel({
  filterEmail,
  title = "Prior Month History",
  id = "history-panel",
}: Props) {
  const [selectedMonth, setSelectedMonth] = useState<{ year: number; month: number } | null>(null);
  const [expandedBookkeeper, setExpandedBookkeeper] = useState<string | null>(null);

  const { data: months = [], isLoading: loadingMonths } = trpc.tierTasks.getHistoryMonths.useQuery();
  const activeMonth = selectedMonth ?? (months.length > 0 ? months[0] : null);

  const { data: history = [], isLoading: loadingHistory } = trpc.tierTasks.getMonthlyHistory.useQuery(
    { year: activeMonth?.year ?? 0, month: activeMonth?.month ?? 0 },
    { enabled: !!activeMonth }
  );

  const filteredHistory = filterEmail
    ? history.filter(r => r.bookkeeperEmail.toLowerCase() === filterEmail.toLowerCase())
    : history;

  const byBookkeeper = filteredHistory.reduce<Record<string, typeof filteredHistory>>((acc, row) => {
    const key = row.bookkeeperEmail;
    if (!acc[key]) acc[key] = [];
    acc[key].push(row);
    return acc;
  }, {});

  const bookkeeperKeys = Object.keys(byBookkeeper).sort();

  return (
    <div
      id={id}
      className="apx-card rounded-xl p-5"
      style={{ background: "oklch(0.98 0.005 240)", border: "1px solid oklch(0.88 0.01 240)" }}
    >
      {/* Panel header */}
      <div className="flex items-center gap-2 mb-4">
        <Clock size={16} style={{ color: "oklch(0.45 0.12 240)" }} />
        <h3
          className="text-base font-semibold"
          style={{ fontFamily: "'Playfair Display', serif", color: "oklch(0.15 0.04 240)" }}
        >
          {title}
        </h3>
      </div>

      {/* Month selector */}
      {loadingMonths ? (
        <p className="text-sm" style={{ color: "oklch(0.45 0.02 240)" }}>Loading months…</p>
      ) : months.length === 0 ? (
        <div
          className="rounded-lg px-4 py-3 text-center"
          style={{ background: "oklch(0.94 0.01 240)", border: "1px solid oklch(0.88 0.01 240)" }}
        >
          <p className="text-sm" style={{ color: "oklch(0.45 0.02 240)" }}>
            No history yet. History is archived automatically when the monthly matrix is reset.
          </p>
        </div>
      ) : (
        <>
          {/* Month tabs */}
          <div className="flex gap-2 flex-wrap mb-4">
            {months.map(m => {
              const isActive = activeMonth?.year === m.year && activeMonth?.month === m.month;
              return (
                <button
                  key={`${m.year}-${m.month}`}
                  onClick={() => setSelectedMonth(m)}
                  className="px-3 py-1 rounded-full text-xs font-semibold transition-all"
                  style={{
                    background: isActive ? "oklch(0.45 0.12 240)" : "oklch(0.92 0.01 240)",
                    color: isActive ? "oklch(0.98 0.005 240)" : "oklch(0.35 0.04 240)",
                    border: `1px solid ${isActive ? "oklch(0.45 0.12 240)" : "oklch(0.82 0.01 240)"}`,
                  }}
                >
                  {MONTH_NAMES[m.month]} {m.year}
                </button>
              );
            })}
          </div>

          {/* History content */}
          {loadingHistory ? (
            <p className="text-sm" style={{ color: "oklch(0.45 0.02 240)" }}>Loading…</p>
          ) : bookkeeperKeys.length === 0 ? (
            <p className="text-sm" style={{ color: "oklch(0.45 0.02 240)" }}>No data for this month.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {bookkeeperKeys.map(email => {
                const rows = byBookkeeper[email];
                const name = rows[0].bookkeeperName;
                const isExpanded = expandedBookkeeper === email;
                const totalCompleted = rows.reduce((s, r) => s + r.completedCount, 0);
                const totalAssigned  = rows.reduce((s, r) => s + r.totalAssigned,  0);

                return (
                  <div
                    key={email}
                    className="rounded-lg overflow-hidden"
                    style={{ border: "1px solid oklch(0.88 0.01 240)", background: "oklch(1 0 0)" }}
                  >
                    {/* Bookkeeper row header */}
                    <button
                      onClick={() => setExpandedBookkeeper(isExpanded ? null : email)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[oklch(0.96_0.005_240)] transition-colors"
                      style={{ background: "transparent", border: "none", cursor: "pointer" }}
                    >
                      {isExpanded
                        ? <ChevronDown  size={14} style={{ color: "oklch(0.45 0.02 240)", flexShrink: 0 }} />
                        : <ChevronRight size={14} style={{ color: "oklch(0.45 0.02 240)", flexShrink: 0 }} />
                      }
                      <Users size={14} style={{ color: "oklch(0.45 0.12 240)", flexShrink: 0 }} />
                      <span className="font-semibold text-sm flex-1" style={{ color: "oklch(0.15 0.04 240)" }}>
                        {name}
                      </span>
                      {/* Tier summary badges */}
                      <div className="flex gap-1.5 items-center">
                        {rows.map(r => {
                          const tc = TIER_COLORS[r.tier];
                          return (
                            <span
                              key={r.tier}
                              className="text-xs px-2 py-0.5 rounded font-semibold"
                              style={{ background: tc?.bg, color: tc?.text, border: `1px solid ${tc?.border}` }}
                            >
                              {r.tier}: {r.completedCount}/{r.totalAssigned}
                            </span>
                          );
                        })}
                        <span
                          className="ml-2 text-xs font-bold"
                          style={{ color: totalCompleted === totalAssigned ? "oklch(0.40 0.15 145)" : "oklch(0.45 0.12 240)" }}
                        >
                          {totalCompleted}/{totalAssigned} total
                        </span>
                      </div>
                    </button>

                    {/* Expanded: per-tier drill-down */}
                    {isExpanded && (
                      <div
                        className="px-4 py-3"
                        style={{ borderTop: "1px solid oklch(0.92 0.01 240)" }}
                      >
                        {rows.map(r => {
                          const tc = TIER_COLORS[r.tier];
                          return (
                            <div key={r.tier} className="mb-4 last:mb-0">
                              <div className="flex items-center gap-2 mb-2">
                                <span
                                  className="text-xs px-2 py-0.5 rounded font-bold"
                                  style={{ background: tc?.bg, color: tc?.text, border: `1px solid ${tc?.border}` }}
                                >
                                  {r.tier}
                                </span>
                                <span className="text-xs" style={{ color: "oklch(0.35 0.04 240)" }}>
                                  {TIER_LABELS[r.tier]}
                                </span>
                                <span className="text-xs ml-auto" style={{ color: "oklch(0.45 0.02 240)" }}>
                                  {r.completedCount} of {r.totalAssigned} completed
                                </span>
                              </div>

                              {r.completedClients.length === 0 ? (
                                <p className="text-xs ml-2" style={{ color: "oklch(0.55 0.02 240)" }}>
                                  No completions recorded.
                                </p>
                              ) : (
                                <div className="flex flex-col gap-1">
                                  {r.completedClients.map((c, i) => (
                                    <div
                                      key={i}
                                      className="flex items-center gap-2 px-2 py-1.5 rounded"
                                      style={{ background: "oklch(0.96 0.005 240)" }}
                                    >
                                      <CheckCircle2 size={12} style={{ color: "oklch(0.40 0.15 145)", flexShrink: 0 }} />
                                      <span className="text-xs flex-1" style={{ color: "oklch(0.15 0.04 240)" }}>
                                        {c.clientName}
                                      </span>
                                      <span className="text-xs" style={{ color: "oklch(0.45 0.02 240)", whiteSpace: "nowrap" }}>
                                        {new Date(c.completedAt).toLocaleString("en-US", {
                                          month: "short", day: "numeric",
                                          hour: "2-digit", minute: "2-digit",
                                        })}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
